/**
 * Map utility functions for geographic calculations, cluster operations, and map management
 */

import maplibregl from 'maplibre-gl';

/**
 * Marketplace search map control corner layout.
 * Change position / sizes / gap here to move or restyle the top-right stack together.
 */
export const MARKETPLACE_MAP_CONTROL_POSITION = 'top-right';

export const MARKETPLACE_MAP_CONTROL_LAYOUT = {
  /** Expand button edge length and zoom stack width (px). */
  size: 48,
  /** Zoom in/out stack height (px). */
  zoomStackHeight: 96,
  /** Vertical gap between expand control and zoom stack (px). */
  gap: 16,
  /**
   * Must match UniversitySearchFilter’s `lg:tw-hidden` (Tailwind `lg` = 1024px).
   * Controls are lowered whenever that selector is visible.
   */
  universitySelectorMaxWidthPx: 1023,
  universitySelectorTopOffset: 75,
};

/**
 * Same visibility rule as UniversitySearchFilter (`lg:tw-hidden`).
 * @returns {boolean}
 */
export function isUniversitySelectorVisible() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }

  return window.matchMedia(
    `(max-width: ${MARKETPLACE_MAP_CONTROL_LAYOUT.universitySelectorMaxWidthPx}px)`
  ).matches;
}

const FULLSCREEN_EXPAND_ICON_PATH =
  'M15.1875 3.375V6.75C15.1875 6.89918 15.1282 7.04226 15.0227 7.14775C14.9173 7.25324 14.7742 7.3125 14.625 7.3125C14.4758 7.3125 14.3327 7.25324 14.2273 7.14775C14.1218 7.04226 14.0625 6.89918 14.0625 6.75V4.73273L11.0855 7.71047C10.9799 7.81602 10.8368 7.87531 10.6875 7.87531C10.5382 7.87531 10.3951 7.81602 10.2895 7.71047C10.184 7.60492 10.1247 7.46177 10.1247 7.3125C10.1247 7.16323 10.184 7.02008 10.2895 6.91453L13.2673 3.9375H11.25C11.1008 3.9375 10.9577 3.87824 10.8523 3.77275C10.7468 3.66726 10.6875 3.52418 10.6875 3.375C10.6875 3.22582 10.7468 3.08274 10.8523 2.97725C10.9577 2.87176 11.1008 2.8125 11.25 2.8125H14.625C14.7742 2.8125 14.9173 2.87176 15.0227 2.97725C15.1282 3.08274 15.1875 3.22582 15.1875 3.375ZM6.91453 10.2895L3.9375 13.2673V11.25C3.9375 11.1008 3.87824 10.9577 3.77275 10.8523C3.66726 10.7468 3.52418 10.6875 3.375 10.6875C3.22582 10.6875 3.08274 10.7468 2.97725 10.8523C2.87176 10.9577 2.8125 11.1008 2.8125 11.25V14.625C2.8125 14.7742 2.87176 14.9173 2.97725 15.0227C3.08274 15.1282 3.22582 15.1875 3.375 15.1875H6.75C6.89918 15.1875 7.04226 15.1282 7.14775 15.0227C7.25324 14.9173 7.3125 14.7742 7.3125 14.625C7.3125 14.4758 7.25324 14.3327 7.14775 14.2273C7.04226 14.1218 6.89918 14.0625 6.75 14.0625H4.73273L7.71047 11.0855C7.81602 10.9799 7.87531 10.8368 7.87531 10.6875C7.87531 10.5382 7.81602 10.3951 7.71047 10.2895C7.60492 10.184 7.46177 10.1247 7.3125 10.1247C7.16323 10.1247 7.02008 10.184 6.91453 10.2895ZM14.625 10.6875C14.4758 10.6875 14.3327 10.7468 14.2273 10.8523C14.1218 10.9577 14.0625 11.1008 14.0625 11.25V13.2673L11.0855 10.2895C10.9799 10.184 10.8368 10.1247 10.6875 10.1247C10.5382 10.1247 10.3951 10.184 10.2895 10.2895C10.184 10.3951 10.1247 10.5382 10.1247 10.6875C10.1247 10.8368 10.184 10.9799 10.2895 11.0855L13.2673 14.0625H11.25C11.1008 14.0625 10.9577 14.1218 10.8523 14.2273C10.7468 14.3327 10.6875 14.4758 10.6875 14.625C10.6875 14.7742 10.7468 14.9173 10.8523 15.0227C10.9577 15.1282 11.1008 15.1875 11.25 15.1875H14.625C14.7742 15.1875 14.9173 15.1282 15.0227 15.0227C15.1282 14.9173 15.1875 14.7742 15.1875 14.625V11.25C15.1875 11.1008 15.1282 10.9577 15.0227 10.8523C14.9173 10.7468 14.7742 10.6875 14.625 10.6875ZM4.73273 3.9375H6.75C6.89918 3.9375 7.04226 3.87824 7.14775 3.77275C7.25324 3.66726 7.3125 3.52418 7.3125 3.375C7.3125 3.22582 7.25324 3.08274 7.14775 2.97725C7.04226 2.87176 6.89918 2.8125 6.75 2.8125H3.375C3.22582 2.8125 3.08274 2.87176 2.97725 2.97725C2.87176 3.08274 2.8125 3.22582 2.8125 3.375V6.75C2.8125 6.89918 2.87176 7.04226 2.97725 7.14775C3.08274 7.25324 3.22582 7.3125 3.375 7.3125C3.52418 7.3125 3.66726 7.25324 3.77275 7.14775C3.87824 7.04226 3.9375 6.89918 3.9375 6.75V4.73273L6.91453 7.71047C7.02008 7.81602 7.16323 7.87531 7.3125 7.87531C7.46177 7.87531 7.60492 7.81602 7.71047 7.71047C7.81602 7.60492 7.87531 7.46177 7.87531 7.3125C7.87531 7.16323 7.81602 7.02008 7.71047 6.91453L4.73273 3.9375Z';

/**
 * Calculate the geographic midpoint of a collection of coordinates
 * @param {Array} coords - Array of coordinate objects with latitude and longitude properties
 * @returns {Object} - Object containing latitude and longitude of the midpoint
 */
export function geographicMidpoint(coords) {
  let x = 0,
    y = 0,
    z = 0;

  coords.forEach(({ latitude, longitude }) => {
    const lat = (latitude * Math.PI) / 180;
    const lon = (longitude * Math.PI) / 180;
    x += Math.cos(lat) * Math.cos(lon);
    y += Math.cos(lat) * Math.sin(lon);
    z += Math.sin(lat);
  });

  const total = coords.length;
  x /= total;
  y /= total;
  z /= total;

  const lon = Math.atan2(y, x);
  const hyp = Math.sqrt(x * x + y * y);
  const lat = Math.atan2(z, hyp);

  return {
    latitude: (lat * 180) / Math.PI,
    longitude: (lon * 180) / Math.PI,
  };
}

/**
 * Get cluster items from a MapLibre cluster source
 * @param {Object} mapSource - MapLibre cluster source
 * @param {number} clusterId - ID of the cluster
 * @param {number} pointCount - Number of points in the cluster
 * @returns {Promise<Array>} - Promise resolving to array of cluster items
 */
export async function getClusterItems(mapSource, clusterId, pointCount) {
  const features = await mapSource.getClusterLeaves(clusterId, pointCount);

  return features.map((leaf) => {
    const p = leaf.properties || {};
    const [longitude, latitude] = leaf.geometry?.coordinates || [];

    return {
      longitude,
      latitude,
      id: p.id,
      price: p.price,
      name: p.name,
      typeString: p.typeString,
      advertType: p.advertType,
      images: p.images,
      roomsAvailableCount: p.roomsAvailableCount,
      numberOfBathRooms: p.numberOfBathRooms,
      spareRoomsAvailableCount: p.spareRoomsAvailableCount,
      travelData: parseTravelData(p.travelData),
      url: p.url,
      isShortlisted: p.isShortlisted,
      featured: p.featured,
    };
  });
}

/**
 * Check if coordinates are within map bounds
 * @param {Array} coord - Array of [longitude, latitude]
 * @param {Object} bounds - MapLibre bounds object
 * @returns {boolean} - True if coordinates are within bounds
 */
export function isCoordinateInBounds(coord, bounds) {
  return (
    coord[0] >= bounds.getWest() &&
    coord[0] <= bounds.getEast() &&
    coord[1] >= bounds.getSouth() &&
    coord[1] <= bounds.getNorth()
  );
}

/**
 * Whether a lng/lat falls inside a centred viewport zone (x and y).
 * @param {Object} map - MapLibre map instance
 * @param {Array} coord - [longitude, latitude]
 * @param {number} zoneFraction - Comfortable zone size as a fraction of viewport (0–1)
 * @returns {boolean}
 */
export function isCoordinateInViewportZone(map, coord, zoneFraction = 1 / 3) {
  if (!map) {
    return false;
  }

  const zoneSize = Math.max(0, Math.min(1, zoneFraction));
  if (zoneSize >= 1) {
    return true;
  }

  const container = map.getContainer();
  if (!container) {
    return false;
  }

  const { width, height } = container.getBoundingClientRect();
  if (!width || !height) {
    return false;
  }

  const point = map.project(coord);
  const marginRatio = (1 - zoneSize) / 2;
  const minX = width * marginRatio;
  const maxX = width * (1 - marginRatio);
  const minY = height * marginRatio;
  const maxY = height * (1 - marginRatio);

  return (
    point.x >= minX &&
    point.x <= maxX &&
    point.y >= minY &&
    point.y <= maxY
  );
}

/**
 * Parse travel data from string or return as-is
 * @param {string|Array} travelData - Travel data that might be a JSON string
 * @returns {Array} - Parsed travel data array
 */
export function parseTravelData(travelData) {
  if (typeof travelData === 'string') {
    try {
      return JSON.parse(travelData);
    } catch (e) {
      console.warn('Failed to parse travelData:', e);
      return [];
    }
  }
  return travelData || [];
}

/**
 * Clear all markers from a marker array
 * @param {Array} markerElements - Array of MapLibre marker elements
 */
export function clearMarkers(markerElements) {
  markerElements.forEach((marker) => {
    marker.remove();
  });
  markerElements.length = 0; // Clear the array
}

/**
 * Create map options object for MapLibre initialization
 * @param {Object} config - Map configuration object
 * @returns {Object} - MapLibre map options
 */
export function createMapOptions(config) {
  const mapOptions = {
    container: config.container,
    style: config.style,
    center: config.center,
    zoom: config.zoom,
    minZoom: config.minZoom,
    maxZoom: config.maxZoom,
  };

  // Add maxBounds if provided
  if (config.maxBounds) {
    mapOptions.maxBounds = config.maxBounds;
  }

  return mapOptions;
}

/**
 * Add cluster source and layers to a MapLibre map
 * @param {Object} map - MapLibre map instance
 * @param {string} sourceId - Source ID for the cluster source
 */
export function addClusterSourceAndLayers(map, sourceId = 'properties') {
  if (!map.getSource(sourceId)) {
    map.addSource(sourceId, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
      cluster: true,
      clusterMaxZoom: 20,
      clusterRadius: 40,
    });
  }

  if (!map.getLayer('clusters')) {
    map.addLayer({
      id: 'clusters',
      type: 'circle',
      source: sourceId,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'point_count'],
          '#93c5fd',
          10,
          '#60a5fa',
          25,
          '#3b82f6',
        ],
        'circle-radius': ['step', ['get', 'point_count'], 16, 10, 20, 25, 24],
        'circle-opacity': 0, // makes the default cluster invisible
      },
    });
  }

  if (!map.getLayer('unclustered-point')) {
    map.addLayer({
      id: 'unclustered-point',
      type: 'circle',
      source: sourceId,
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-radius': 10,
        'circle-opacity': 0, // makes the default cluster invisible
      },
    });
  }
}

/**
 * Update cluster source data with properties
 * @param {Object} map - MapLibre map instance
 * @param {Array} properties - Array of property objects
 * @param {string} sourceId - Source ID for the cluster source
 */
export function updateClusterSource(map, properties, sourceId = 'properties') {
  const features = properties.map((m) => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [m.longitude, m.latitude] },
    properties: {
      id: m.id,
      price: m.price,
      name: m.name,
      typeString: m.typeString,
      advertType: m.advertType,
      images: m.images,
      roomsAvailableCount: m.bedrooms,
      numberOfBathRooms: m.bathrooms,
      spareRoomsAvailableCount: m.spareRoomsAvailableCount,
      travelData: m.travelData,
      url: m.url,
      isShortlisted: m.isShortlisted,
      featured: m.featured,
    },
  }));

  const fc = { type: 'FeatureCollection', features };
  const src = map.getSource(sourceId);
  if (src) src.setData(fc);
}

/**
 * MapLibre IControl: full-screen expand as its own top-right sibling of the nav group.
 * @param {{ onClick?: () => void, size?: number }} [options]
 * @returns {import('maplibre-gl').IControl}
 */
export function createFullscreenExpandControl(options = {}) {
  const size = options.size ?? MARKETPLACE_MAP_CONTROL_LAYOUT.size;
  const onClick = typeof options.onClick === 'function' ? options.onClick : null;

  return {
    onAdd() {
      const container = document.createElement('div');
      container.className = 'maplibregl-ctrl marketplace-map-fullscreen-ctrl';

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'marketplace-map-fullscreen-button';
      button.setAttribute('data-testid', 'marketplace-map-fullscreen-button');
      button.setAttribute('aria-label', 'Full screen map');
      button.style.cssText = `
        display: flex;
        align-items: center;
        justify-content: center;
        width: ${size}px;
        height: ${size}px;
        padding: 0;
        border: 0;
        border-radius: 9999px;
        background: white;
        color: #000;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        cursor: pointer;
        transition: transform 0.15s ease, box-shadow 0.15s ease;
      `;

      button.addEventListener('mouseenter', () => {
        button.style.transform = 'scale(1.05)';
        button.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
      });
      button.addEventListener('mouseleave', () => {
        button.style.transform = 'scale(1)';
        button.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
      });
      button.addEventListener('mousedown', () => {
        button.style.transform = 'scale(0.95)';
      });
      button.addEventListener('mouseup', () => {
        button.style.transform = 'scale(1.05)';
      });

      const svgNs = 'http://www.w3.org/2000/svg';
      const svg = document.createElementNS(svgNs, 'svg');
      svg.setAttribute('xmlns', svgNs);
      svg.setAttribute('width', '20');
      svg.setAttribute('height', '20');
      svg.setAttribute('viewBox', '0 0 18 18');
      svg.setAttribute('fill', 'none');
      svg.setAttribute('aria-hidden', 'true');
      svg.style.cssText = 'pointer-events: none; width: 20px; height: 20px;';

      const path = document.createElementNS(svgNs, 'path');
      path.setAttribute('d', FULLSCREEN_EXPAND_ICON_PATH);
      path.setAttribute('fill', 'currentColor');
      svg.appendChild(path);
      button.appendChild(svg);

      if (onClick) {
        button.addEventListener('click', onClick);
      }

      container.appendChild(button);
      this._container = container;
      this._button = button;
      this._onClick = onClick;

      return container;
    },

    onRemove() {
      if (this._button && this._onClick) {
        this._button.removeEventListener('click', this._onClick);
      }
      this._container?.parentNode?.removeChild(this._container);
      this._container = null;
      this._button = null;
      this._onClick = null;
    },
  };
}

/**
 * Style MapLibre navigation control
 * @param {HTMLElement} container - Map container element
 * @param {boolean} isSinglePropertyMapPage
 * @param {{ width?: number, height?: number, marginTop?: number }} [options]
 */
export function styleNavigationControl(
  container,
  isSinglePropertyMapPage = false,
  options = {}
) {
  const navControl = container.querySelector('.maplibregl-ctrl-group');
  if (!navControl) return;

  const cleanupFunctions = [];
  const width = options.width ?? 40;
  const height = options.height ?? 80;
  const radius = Math.round(width / 2);

  let marginTop;
  if (typeof options.marginTop === 'number') {
    marginTop = options.marginTop;
  } else if (!isSinglePropertyMapPage && isUniversitySelectorVisible()) {
    marginTop = MARKETPLACE_MAP_CONTROL_LAYOUT.universitySelectorTopOffset;
  }

  let navControlStyle = `
    background: white !important;
    border-radius: ${radius}px !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
    border: none !important;
    padding: 4px !important;
    width: ${width}px !important;
    height: ${height}px !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    justify-content: center !important;
`;

  if (typeof marginTop === 'number') {
    navControlStyle += `margin-top: ${marginTop}px !important;`;
  }

  // Apply custom styling to match the design
  navControl.style.cssText = navControlStyle;

  // Style the zoom buttons
  const zoomButtons = navControl.querySelectorAll(
    '.maplibregl-ctrl-zoom-in, .maplibregl-ctrl-zoom-out'
  );
  zoomButtons.forEach((button) => {
    button.style.cssText = `
      background: transparent !important;
      border: none !important;
      width: 32px !important;
      height: 32px !important;
      border-radius: 16px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      cursor: pointer !important;
      transition: all 0.2s ease !important;
      color: #3b82f6 !important;
      margin: 0 !important;
      -webkit-tap-highlight-color: transparent !important;
    `;

    const pressStart = () => {
      button.style.transform = 'scale(0.9)';
      button.style.backgroundColor = '#e0f2fe';
    };

    const pressEnd = () => {
      button.style.transform = 'scale(1)';
      button.style.backgroundColor = 'transparent';
    };

    button.addEventListener('mousedown', pressStart);
    button.addEventListener('mouseup', pressEnd);
    button.addEventListener('mouseleave', pressEnd);

    // Mobile / touch events
    button.addEventListener('touchstart', pressStart, { passive: true });
    button.addEventListener('touchend', pressEnd, { passive: true });
    button.addEventListener('touchcancel', pressEnd, { passive: true });

    // Store cleanup function
    cleanupFunctions.push(() => {
      button.removeEventListener('mousedown', pressStart);
      button.removeEventListener('mouseup', pressEnd);
      button.removeEventListener('mouseleave', pressEnd);
      button.removeEventListener('touchstart', pressStart);
      button.removeEventListener('touchend', pressEnd);
      button.removeEventListener('touchcancel', pressEnd);
    });
  });

  // Add divider between buttons
  const zoomInButton = navControl.querySelector('.maplibregl-ctrl-zoom-in');
  const zoomOutButton = navControl.querySelector('.maplibregl-ctrl-zoom-out');

  if (
    zoomInButton &&
    zoomOutButton &&
    !navControl.querySelector('[data-marketplace-zoom-divider]')
  ) {
    const divider = document.createElement('div');
    divider.setAttribute('data-marketplace-zoom-divider', 'true');
    divider.style.cssText = `
      width: 100% !important;
      height: 1px !important;
      background-color: #e5e7eb !important;
      margin: 4px 0 !important;
    `;
    zoomInButton.parentNode.insertBefore(divider, zoomOutButton);
  }

  // Return cleanup function
  return () => {
    cleanupFunctions.forEach((cleanup) => cleanup());
  };
}

/**
 * Generic flyTo function for MapLibre maps
 * @param {Object} map - MapLibre map instance
 * @param {Object} options - FlyTo options
 * @param {Array} options.center - [longitude, latitude] coordinates
 * @param {number} [options.zoom] - Target zoom level
 * @param {number} [options.zoomOffset] - Zoom offset from current zoom (e.g., +1)
 * @param {boolean} [options.essential] - Whether animation is essential for accessibility
 * @param {number} [options.southOffset] - Base offset south to account for UI overlay (default: 0.001)
 * @param {boolean} [options.scaleOffsetWithZoom] - Whether to scale offset with zoom level (default: true)
 */
export function flyTo(map, options) {
  if (!map) return;

  const {
    center,
    zoom,
    zoomOffset = 0,
    essential = true,
    southOffset = 0.0015,
    scaleOffsetWithZoom = true,
  } = options;

  // Calculate the target zoom level, round then add a small margin to the calculated zoom to avoid issues where two layers of clusters are showing.
  let targetZoom;
  if (zoom !== undefined) {
    targetZoom = zoom;
  } else if (zoomOffset !== 0) {
    targetZoom = Math.round(map.getZoom()) + zoomOffset + 0.01;
  } else {
    targetZoom = Math.round(map.getZoom()) + 0.01;
  }

  // Scale the south offset with zoom level to account for UI overlay
  // Higher zoom = smaller offset needed, lower zoom = larger offset needed
  let scaledSouthOffset = southOffset;
  if (scaleOffsetWithZoom) {
    // Scale factor: 2^(11 - zoom) where 11 is our reference zoom level
    // This means at zoom 11, offset = baseOffset, at zoom 12, offset = 0.5x baseOffset, etc.
    const zoomScaleFactor = Math.pow(2, 11 - targetZoom);
    scaledSouthOffset = southOffset * zoomScaleFactor;
  }

  const flyToOptions = {
    center: [center[0], center[1] - scaledSouthOffset],
    essential,
  };

  // Add zoom to options if specified
  if (zoom !== undefined || zoomOffset !== 0) {
    flyToOptions.zoom = targetZoom;
  }

  map.flyTo(flyToOptions);
}

export function updateAttributionText(refs) {
  // Target the maplibregl-ctrl-attrib-inner class and replace text with OSM attribution
  const attributionInner = refs.maplibreContainer.querySelector(
    '.maplibregl-ctrl-attrib-inner'
  );
  if (attributionInner) {
    attributionInner.innerHTML =
      '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';
  }
}

/**
 * @param {object} mapOptions
 * @param {{
 *   showFullscreenExpand?: boolean,
 *   onFullscreenExpand?: () => void,
 *   controlPosition?: string,
 * }} [controlOptions]
 */
export function initMap(mapOptions, controlOptions = {}) {
  const map = new maplibregl.Map(mapOptions);
  const position =
    controlOptions.controlPosition || MARKETPLACE_MAP_CONTROL_POSITION;

  // Expand first so it stacks above NavigationControl in the corner.
  if (controlOptions.showFullscreenExpand) {
    map.addControl(
      createFullscreenExpandControl({
        onClick: controlOptions.onFullscreenExpand,
        size: MARKETPLACE_MAP_CONTROL_LAYOUT.size,
      }),
      position
    );
  }

  map.addControl(
    new maplibregl.NavigationControl({ showCompass: false }),
    position
  );

  return map;
}

/**
 * Push the top-right control stack below the university select on small screens.
 * When expand is present it is the first control (gets the offset); zoom keeps `gap`.
 * @param {HTMLElement} container
 * @param {{
 *   isSinglePropertyMapPage?: boolean,
 *   showFullscreenExpand?: boolean,
 * }} [options]
 */
export function applyMarketplaceMapControlStackOffset(container, options = {}) {
  if (!container) return;

  const { gap, universitySelectorTopOffset } = MARKETPLACE_MAP_CONTROL_LAYOUT;
  const showFullscreenExpand = Boolean(options.showFullscreenExpand);
  const isSinglePropertyMapPage = options.isSinglePropertyMapPage === true;
  const stackOffset =
    !isSinglePropertyMapPage && isUniversitySelectorVisible()
      ? universitySelectorTopOffset
      : null;

  const expandCtrl = container.querySelector(
    '.marketplace-map-fullscreen-ctrl'
  );
  const navControl = container.querySelector('.maplibregl-ctrl-group');

  if (expandCtrl) {
    expandCtrl.style.marginTop =
      stackOffset != null ? `${stackOffset}px` : '';
  }

  if (!navControl) return;

  if (showFullscreenExpand) {
    // Sibling below expand — only the inter-control gap.
    navControl.style.marginTop = `${gap}px`;
  } else if (stackOffset != null) {
    navControl.style.marginTop = `${stackOffset}px`;
  } else {
    navControl.style.marginTop = '';
  }
}

/**
 * Style the marketplace search map control stack (expand + zoom) together.
 * @param {HTMLElement} container
 * @param {{
 *   isSinglePropertyMapPage?: boolean,
 *   showFullscreenExpand?: boolean,
 * }} [options]
 */
export function styleMarketplaceMapControls(container, options = {}) {
  const { size, zoomStackHeight, gap } = MARKETPLACE_MAP_CONTROL_LAYOUT;
  const showFullscreenExpand = Boolean(options.showFullscreenExpand);
  const isSinglePropertyMapPage = options.isSinglePropertyMapPage === true;

  // Size the zoom pill; stack offsets (incl. mobile university clearance) applied after.
  const cleanupNav = styleNavigationControl(
    container,
    isSinglePropertyMapPage,
    showFullscreenExpand
      ? { width: size, height: zoomStackHeight, marginTop: gap }
      : undefined
  );

  applyMarketplaceMapControlStackOffset(container, {
    showFullscreenExpand,
    isSinglePropertyMapPage,
  });

  return cleanupNav;
}
